import { useEffect, useState } from 'react';
import { FocusTrap } from 'focus-trap-react';
import { FOCUS_TRAP_OPTIONS } from '@/constants/focus-trap';
import { LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LogoutConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function LogoutConfirmModal({ open, onClose, onConfirm }: LogoutConfirmModalProps) {
  // Two-phase: mount + animate in
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      // Allow mount to happen, then animate in
      requestAnimationFrame(() => { setVisible(true); });
    } else {
      setVisible(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <FocusTrap focusTrapOptions={FOCUS_TRAP_OPTIONS}>
      <div
        className={cn(
          'fixed inset-0 z-sime-modal flex items-center justify-center p-4',
          'transition-opacity duration-150',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-sime-overlay" />
        <div
          onClick={(e) => { e.stopPropagation(); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-confirm-title"
          className={cn(
            'relative w-full max-w-[380px] rounded-2xl p-6 bg-sime-surface',
            'shadow-neu-xl',
            'transition-all duration-150',
            visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
          )}
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-xl bg-sime-danger/10">
              <LogOut className="h-5 w-5 text-sime-danger" />
            </div>
            <div>
              <h3
                id="logout-confirm-title"
                className="text-[17px] font-semibold text-sime-text-primary"
              >
                Cerrar sesion
              </h3>
              <p className="text-sm text-sime-text-secondary mt-1">
                Esta seguro que desea cerrar su sesion? Debera iniciar sesion nuevamente para acceder al sistema.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-sime-text-secondary hover:text-sime-text-primary transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-sime-danger hover:opacity-90 transition-opacity"
            >
              Cerrar sesion
            </button>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
}
